import { Buffer } from 'buffer'
import mime from 'mime'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import * as amplitude from '@amplitude/analytics-browser'

/** @typedef {typeof util} Util */

export const util = {
  isObject(input) {
    return input && !Array.isArray(input) && typeof input === 'object'
  },
  escapeHTML(s) {
    let lookup = {
      '&': '&amp;',
      '"': '&quot;',
      "'": '&apos;',
      '<': '&lt;',
      '>': '&gt;',
    }
    return s.replace(/[&"'<>]/g, (c) => lookup[c])
  },
  apiURL() {
    return process.env.VUE_APP_API_URL
  },
  identifyAnalyticsUser(identifyData, userId) {
    amplitude.setUserId(userId)

    const identifyEvent = new amplitude.Identify()
    const safeData = identifyData || {}
    Object.entries(safeData)
      .filter(([_, value]) => value !== null && value !== undefined)
      .forEach(([key, value]) => {
        identifyEvent.set(key, value)
      })
    amplitude.identify(identifyEvent)
  },
  prepareAnalyticsUserProperties(physicianStore) {
    const physicianData = storeToRefs(physicianStore)
    return {
      userMail: physicianData.physician.value?.email,
      userSpecialties: physicianData.physicianSpecialties.value?.map((item) => item.typeName),
      userCrms: physicianData.crms.value,
      subscribedExtensions: physicianData.extensionSubscriptions.value?.map((item) => item.extension.name),
      isTester: physicianData.physician.value?.isTester,
      hasAddress: physicianData.addresses.value && physicianData.addresses.value.length > 0,
      userAddresses: physicianData.addresses.value,
    }
  },
  faviconPerDomain(domain) {
    const faviconMap = {
      ec: '/favicon_ec.ico',
      dermsource: '/favicon_sbd.png',
      sw: '/favicon_shortway.ico',
      medmentor: '/favicon_kintsu.svg',
    }

    const pharmaPrefix = domain.split(/[\.-]/)[0]

    return faviconMap[pharmaPrefix] || '/favicon_kintsu.svg'
  },
  copy(obj) {
    if (!obj || typeof obj !== 'object') {
      return obj
    }
    return JSON.parse(JSON.stringify(obj))
  },
  clearString(string) {
    return !string
      ? null
      : string
          .toString()
          .trim()
          .replace(/\u2010|\u2013|\u2014/g, '-') || null
  },
  clearStringValues(object) {
    if (typeof object !== 'object' || Array.isArray(object)) {
      return object
    }
    for (const key in object) {
      const value = object[key]
      if (typeof value === 'string') {
        object[key] = this.clearString(value)
      } else if (typeof value === 'object') {
        this.clearStringValues(value)
      }
    }
    return object
  },
  neatString(string, trim = true) {
    return string === null || string === undefined ? '' : trim ? string.toString().trim() : string.toString()
  },
  removeAccents(string) {
    if (!string) {
      return string
    }

    return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  },
  toComparableStringLowerCase(string) {
    return this.removeAccents(this.neatString(string)).toLowerCase()
  },
  hasTermInAnyString(term, ...strings) {
    term = this.toComparableStringLowerCase(term)
    return strings
      .map((string) => this.toComparableStringLowerCase(string))
      .filter((string) => !!string)
      .some((string) => string.includes(term))
  },
  datetimeSorter(asc = true, format = 'YYYY-MM-DDTHH:mm:ss') {
    return asc
      ? (dt1, dt2) => moment(dt1, format, true).diff(moment(dt2, format, true))
      : (dt1, dt2) => moment(dt2, format, true).diff(moment(dt1, format, true))
  },
  stringSorter(asc = true) {
    return asc
      ? (s1, s2) => {
          s1 = this.toComparableStringLowerCase(s1)
          s2 = this.toComparableStringLowerCase(s2)
          return s1.localeCompare(s2)
        }
      : (s1, s2) => {
          s1 = this.toComparableStringLowerCase(s1)
          s2 = this.toComparableStringLowerCase(s2)
          return s2.localeCompare(s1)
        }
  },
  objectSorter(mapFunction, asc = true) {
    return (obj1, obj2) => this.stringSorter(asc)(mapFunction(obj1), mapFunction(obj2))
  },
  deepObjectValue(obj, route) {
    if (!obj || !route || typeof route !== 'string') {
      return obj
    }
    return route.split('.').reduce((obj, key) => (obj ? obj[key] : null), obj)
  },
  formatCurrency(number) {
    if (number == undefined || number == null) {
      number = 0
    }

    if (typeof number !== 'number') {
      return number
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(number)
  },
  formatCpf(cpf) {
    if (!cpf || !/[0-9]{11}/.test(cpf)) {
      return cpf
    }

    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`
  },

  maskCpf(cpf) {
    const unformatedCpf = cpf ? cpf.split('.').join('').split('-').join('') : ''
    if (!cpf || !/[0-9]{11}/.test(unformatedCpf)) {
      return cpf
    }
    return `${unformatedCpf.slice(0, 3)}.***.***-${unformatedCpf.slice(9)}`
  },

  formatCnpj(cnpj) {
    if (!cnpj || !/[0-9]{14}/.test(cnpj)) {
      return cnpj
    }

    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
  },
  formatCpfCnpj(cpfCnpj) {
    if (/[0-9]{11}/.test(cpfCnpj)) {
      this.formatCpf(cpfCnpj)
    } else if (!cnpj || !/[0-9]{14}/.test(cnpj)) {
      this.formatCnpj(cpfCnpj)
    } else {
      return cpfCnpj
    }
  },
  getTimestamp() {
    return new Date().getTime()
  },
  getDatesInRange({ dateRange, outputFormat = null, inputFormat = 'YYYY-MM-DD' }) {
    let startDateMoment, endDateMoment
    if (
      !Array.isArray(dateRange) ||
      dateRange.length !== 2 ||
      !(startDateMoment = moment(dateRange[0], inputFormat, true)).isValid() ||
      !(endDateMoment = moment(dateRange[1], inputFormat, true)).isValid()
    ) {
      return []
    }
    const dates = []
    for (let date = startDateMoment; date.isSameOrBefore(endDateMoment, 'day'); date.add(1, 'day')) {
      dates.push(!outputFormat ? moment(date) : date.format(outputFormat))
    }
    return dates
  },
  getTimeWithinQuarter(time, start = true) {
    const timeMoment = moment(time, 'HH:mm', true)
    if (!timeMoment.isValid()) {
      return null
    }

    timeMoment.minutes(Math.floor((timeMoment.minutes() / 15) * (start ? 1 : -1)) * 15 * (start ? 1 : -1))
    return timeMoment
  },
  getTimesInRangeGivenIntervalInMinutes({
    startTime,
    endTime,
    intervalMinutes,
    inputFormat = 'HH:mm',
    outputFormat = null,
  }) {
    let startTimeMoment, endTimeMoment
    if (
      !startTime ||
      !endTime ||
      intervalMinutes <= 0 ||
      intervalMinutes > 24 * 60 ||
      !(startTimeMoment = moment(startTime, inputFormat, true)).isValid() ||
      !(endTimeMoment = moment(endTime, inputFormat, true)).isValid()
    ) {
      return []
    }

    const totalMinutes = endTimeMoment.diff(startTimeMoment, 'minute') || 1440
    const times = []
    for (let addedMinutes = 0; addedMinutes <= totalMinutes; addedMinutes += intervalMinutes) {
      const time = moment(startTimeMoment).add(addedMinutes, 'minute')
      times.push(!outputFormat ? time : time.format(outputFormat))
    }
    return times
  },
  periodsOverlap(period1Start, period1End, period2Start, period2End, format = 'YYYY-MM-DD') {
    const period1StartMoment = moment(period1Start, format, true)
    const period1EndMoment = moment(period1End, format, true)
    const period2StartMoment = moment(period2Start, format, true)
    const period2EndMoment = moment(period2End, format, true)
    return period1StartMoment.isBefore(period2EndMoment) && period1EndMoment.isAfter(period2StartMoment)
  },
  dateToMoment(date) {
    const momentDate = moment(date, /\d{2}\/\d{2}\/\d{4}/.test(date) ? 'DD/MM/YYYY' : 'YYYY-MM-DD', true)
    return momentDate.isValid() ? momentDate : null
  },
  convertToHtml(text, defaultText) {
    return !text ? defaultText || '' : text.replace(/\n/g, '<br />')
  },
  getYesNoConverter(keyName) {
    return (item) => (item[keyName] ? 'Sim' : 'Não')
  },
  toIsoDate(date) {
    return !date || !/\d{2}\/\d{2}\/\d{4}/.test(date) ? null : date.split('/').reverse().join('-')
  },
  toFormattedDate(date) {
    if (!!date) {
      date = date.split('T')[0]
    }
    return !date || !/\d{4}-\d{2}-\d{2}/.test(date) ? null : date.split('-').reverse().join('/')
  },
  toByteSize(bytes, truncate = false) {
    if (bytes === 0) return '0 Bytes'
    const kilobyte = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    const sizeIndex = Math.floor(Math.log(bytes) / Math.log(kilobyte))
    const formatter = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: truncate ? 0 : 1,
    })
    return formatter.format(bytes / Math.pow(kilobyte, sizeIndex)) + ' ' + sizes[sizeIndex]
  },
  toShortWeekday(weekdayNumber) {
    if (weekdayNumber < 0 || weekdayNumber > 6) {
      return undefined
    }

    return ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][weekdayNumber]
  },
  toWeekday(weekdayNumber) {
    if (weekdayNumber < 0 || weekdayNumber > 6) {
      return undefined
    }

    return ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'][weekdayNumber]
  },
  toShortTime(time) {
    if (!time) {
      return time
    }

    return time.split(':').slice(0, 2).join(':')
  },
  toNumbersOnly(value) {
    if (!value) {
      return value
    }

    return value.replace(/[^0-9]/g, '')
  },
  formatEnumText(value) {
    return `${value}`.replace('_', ' ')
  },
  maskPhone(value) {
    if (!value || value == '---') {
      return value
    }

    const phoneNumber = this.toNumbersOnly(value)

    const result = `(${phoneNumber.substring(0, 2)}) *****-****`
    return result
  },
  toAge(birthday) {
    const momentBirthday = this.dateToMoment(birthday)
    if (!momentBirthday) {
      return momentBirthday
    }
    const years = moment().diff(momentBirthday, 'year')
    return years === 1 ? '1 ano' : `${years} anos`
  },
  toAgeNumber(birthday) {
    const momentBirthday = this.dateToMoment(birthday)
    if (!momentBirthday) {
      return momentBirthday
    }
    const years = moment().diff(momentBirthday, 'year')
    return years
  },
  /**
   * @param {number} timeInMillis
   * @returns {Promise<void>}
   */
  waitSomeTime(timeInMillis) {
    return new Promise((resolve) => setTimeout(resolve, timeInMillis))
  },
  copyToClipboard(text) {
    navigator.clipboard.writeText(text)
  },
  urlEncode(text) {
    if (!text) {
      return text
    }

    return encodeURIComponent(text)
  },
  isValidUrl(urlString) {
    var urlPattern = new RegExp(
      '^(https?:\\/\\/)?' +
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
        '((\\d{1,3}\\.){3}\\d{1,3}))' +
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
        '(\\?[;&a-z\\d%_.~+=-]*)?' +
        '(\\#[-a-z\\d_]*)?$',
      'i',
    )
    return !!urlPattern.test(urlString)
  },
  calcImc(weight, height) {
    const w = Number(weight)
    let h = Number(height)
    if (!w || !h) {
      return null
    }
    if (h > 100) {
      h = h / 100
    }

    return w / (h * h)
  },
  /**
   * @param {string} dataurl
   * @param {string} filename
   * @returns {File | undefined}
   */
  dataURLtoFile(dataurl, filename) {
    let [meta, base64] = dataurl.split(',')
    const mimeType = meta.match(/data:(.+);base64/)[1]
    if (!base64 || !mimeType) {
      return undefined
    }
    const buffer = Buffer.from(base64, 'base64')
    const ext = mime.getExtension(mimeType) ?? 'png'
    return new File([buffer], `${filename}.${ext}`, { type: mime })
  },

  /**
   * @param {Blob} blob
   * @param {string} name
   */
  downloadBlob(blob, name) {
    const pdfData = URL.createObjectURL(blob)
    const pdfLink = document.createElement('a')
    pdfLink.href = pdfData
    pdfLink.download = name
    pdfLink.target = '_blank'
    pdfLink.click()
    URL.revokeObjectURL(blob)
    pdfLink.remove()
  },

  createRandomString(length) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    const randomArray = new Uint8Array(length)
    crypto.getRandomValues(randomArray)
    randomArray.forEach((number) => {
      result += chars[number % chars.length]
    })
    return result
  },
}

export default {
  install(VueInstance, options) {
    VueInstance.prototype.$util = util
  },
}

String.prototype.firstLetterToLowercase = function () {
  let text = this.valueOf()
  if (!text || text.length === 0) {
    return text
  }

  return text.charAt(0).toLowerCase() + text.slice(1)
}

export function useUtil() {
  return util
}
